import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { api, externalApi } from '../../../../services/authService';
import { selectCurrentToken } from '../../../../redux/authSlice';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import MessageTemplateService from '../../../../services/messageTemplateService';
import moment from 'moment-timezone';
import BasicInfoForm from '../../../../components/campaigns/BasicInfoForm';
import TestCampaignSection from '../../../../components/campaigns/TestCampaignSection';
import CampaignHeader from '../../../../components/campaigns/CampaignHeader';
import ReminderCampaignConfig from '../../../../components/campaigns/reminder/ReminderCampaignConfig';
import CommunicationEndpointsProvider from '../../../../components/campaigns/CommunicationEndpointsProvider';
import { createDefaultReminderCampaign, serializeReminderCampaign } from '../../../../utils/campaignSerializers';
import ReminderTestConfig from '../../../../components/campaigns/reminder/ReminderTestConfig';
import OptOutConfig from '../../../../components/campaigns/OptOutConfig';
import RetryConfigurationPanel from '../../../../components/campaigns/RetryConfigurationPanel';
import { formatRetryConfigForBackend } from '../../../../utils/retryStrategyUtils';

// Get list of timezones from moment-timezone and format them for display
const timezones = moment.tz.names().map(zone => {
  const offset = moment.tz(zone).format('Z');
  const label = `(UTC${offset}) ${zone.replace(/_/g, ' ')}`;
  return {
    value: zone,
    label
  };
});

const steps = [
  'Basic Information',
  'Message Configuration & Schedule Settings',
  'Test',
  'Review & Activate'
];

const CreateReminderCampaign = () => {
  const navigate = useNavigate();
  const token = useSelector(selectCurrentToken);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [crmCampaigns, setCrmCampaigns] = useState([]);
  const [statusChoices, setStatusChoices] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [communicationEndpoints, setCommunicationEndpoints] = useState([]);

  // Form state
  const [formData, setFormData] = useState(createDefaultReminderCampaign());

  const [testData, setTestData] = useState({
    email: '',
    phone: '',
    testStatus: null,
    testError: null
  });

  // Load accounts, templates, and status choices on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accountsResponse, templatesResponse, statusResponse] = await Promise.all([
          api.get('/crm/accounts/'),
          MessageTemplateService.getTemplates({ channel: formData.channel }),
          api.get('/acs/nurturing-campaigns/status_choices/')
        ]);
        setAccounts(accountsResponse.data);
        setTemplates(templatesResponse);
        setStatusChoices(statusResponse.data);
      } catch (err) {
        setError('Failed to load required data');
        console.error('Error loading data:', err);
      }
    };
    fetchData();
  }, [formData.channel]);

  // Fetch CRM campaigns when account changes
  useEffect(() => {
    const fetchCrmCampaigns = async () => {
      if (formData.account) {
        try {
          const response = await api.get(`/crm/accounts/${formData.account}/campaigns/`);
          setCrmCampaigns(response.data);
        } catch (err) {
          setError('Failed to load CRM campaigns');
          console.error('Error loading CRM campaigns:', err);
        }
      } else {
        setCrmCampaigns([]);
      }
    };
    fetchCrmCampaigns();
  }, [formData.account]);

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value,
    }));
  };

  const handleCampaignChange = (updatedCampaign) => {
    // Handle channel switching with voice config preservation
    if (updatedCampaign.channel && updatedCampaign.channel !== formData.channel) {
      // When switching to voice, ensure proper voice config structure
      if (updatedCampaign.channel === 'voice') {
        const updatedSchedule = {
          ...updatedCampaign.reminder_schedule,
          reminder_times: updatedCampaign.reminder_schedule.reminder_times.map(time => ({
            ...time,
            messages: time.messages.map(message => ({
              ...message,
              // Ensure voice config has proper structure (no IDs for new campaigns)
              voice_config: {
                content: '',
                from_endpoint: '',
                platform: 'bland_ai',
                voice_id: 'alexa',
                voice_name: 'Alexa (Female)',
                temperature: 0.7,
                max_duration: 5,
                retry_attempts: 3,
                voicemail_message: '',
                voicemail_action: 'hangup',
                record_call: false,
                call_timeout: 60,
                platform_config: {
                  task: '',
                  pathway_id: '',
                  pathway_version: 1,
                  first_sentence: '',
                  background_track: '',
                  model: 'base',
                  wait_for_greeting: false,
                  block_interruptions: false,
                  interruption_threshold: 100,
                  sensitive_voicemail_detection: false,
                  transfer_phone_number: '',
                  transfer_list: {},
                  language: 'en-US',
                  voicemail_sms: {},
                  dispatch_hours: {},
                  ignore_button_press: false,
                  timezone: 'America/New_York',
                  request_data: {},
                  tools: [],
                  start_time: '',
                  voicemail_action: {},
                  retry: { wait: 300, voicemail_action: 'leave_message' },
                  max_duration: 5,
                  record: false,
                  webhook: '',
                  webhook_events: [],
                  metadata: {},
                  citation_schema_id: '',
                  analysis_preset: 'default',
                  available_tags: [],
                  geospatial_dialing: 'disabled',
                  precall_dtmf_sequence: '',
                  dynamic_data: [],
                  keywords: [],
                  pronunciation_guide: [],
                  local_dialing: false
                },
                webhook_config: {
                  webhook: '',
                  webhook_events: []
                },
                metadata: {}
              }
            }))
          }))
        };
        updatedCampaign.reminder_schedule = updatedSchedule;
      }
    }

    setFormData(prev => ({
      ...prev,
      ...updatedCampaign,
    }));
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (activate = false) => {
    setLoading(true);
    setError(null);

    try {
      // Serialize the campaign data
      const serializedCampaign = serializeReminderCampaign({
        ...formData,
        status: activate ? 'active' : 'draft'
      });

      // Add properly formatted retry configuration
      const retryConfig = formatRetryConfigForBackend(formData.retry_strategy_id, formData.max_retries);
      const finalPayload = {
        ...serializedCampaign,
        ...retryConfig
      };

      // Create the campaign
      const campaignResponse = await api.post('/acs/reminder-campaigns/', finalPayload);

      // If activating, make the activation API call
      if (activate) {
        await api.post(`/acs/nurturing-campaigns/${campaignResponse.data.id}/activate/`);
      }

      // Navigate to lead selection page
      navigate(`/crm/lead-nurturing/${campaignResponse.data.id}/lead-selection`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
      setCreateDialogOpen(false);
    }
  };

  const handleTestCampaign = async (testData) => {
    setTestData(prev => ({ ...prev, testStatus: 'loading', testError: null }));
    try {
      if (testData.error) {
        throw new Error(testData.error);
      }

      const selectedReminder = formData.reminder_schedule.reminder_times[testData.reminderIndex];
      const message = selectedReminder.messages[0];

      if (formData.channel === 'voice') {
        // Voice-specific test handling
        const voiceConfig = message.voice_config;
        const fromEndpoint = voiceConfig.from_endpoint;

        if (!fromEndpoint) {
          throw new Error('From endpoint is required for voice campaigns');
        }

        // Platform-specific test payload
        const testPayload = {
          phone_number: testData.phone,
          platform: voiceConfig.platform,
          voice_id: voiceConfig.voice_id,
          temperature: voiceConfig.temperature,
          max_duration: voiceConfig.max_duration,
          from_endpoint: fromEndpoint,
          platform_config: voiceConfig.platform_config,
          webhook_config: voiceConfig.webhook_config
        };

        const response = await externalApi.post('/communications/send-direct-voice/', testPayload);
        
        if (response.data.success) {
          setTestData(prev => ({ 
            ...prev, 
            testStatus: 'success', 
            testError: null 
          }));
        } else {
          throw new Error(response.data.detail || 'Failed to send test voice call');
        }
      } else {
        // Handle other channels (SMS, email, chat)
        const testPayload = {
          phone_number: testData.phone,
          message: testData.message,
          twilio_number: testData.twilio_number
        };
        
        const response = await externalApi.post('/communications/send-direct-sms/', testPayload);
        
        if (response.data.success) {
          setTestData(prev => ({ 
            ...prev, 
            testStatus: 'success', 
            testError: null 
          }));
        } else {
          throw new Error(response.data.detail || 'Failed to send test message');
        }
      }
    } catch (err) {
      console.error('Test Campaign - Error:', err);
      setTestData(prev => ({
        ...prev,
        testStatus: 'error',
        testError: err.response?.data?.detail || err.message || 'Failed to send test message'
      }));
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <BasicInfoForm
            formData={formData}
            onInputChange={handleInputChange}
            accounts={accounts}
            crmCampaigns={crmCampaigns}
            campaignTypes={{ reminder: 'Reminder Campaign' }}
          />
        );

      case 1:
        return (
          <CommunicationEndpointsProvider
            accountId={formData.account}
            campaignId={formData.crm_campaign}
            channel={formData.channel}
            activeTab={activeStep}
          >
            {({ endpoints, loading: endpointsLoading, error: endpointsError }) => (
              <Grid container spacing={3}>
                {endpointsError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{endpointsError}</Alert>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <ReminderCampaignConfig
                    campaign={formData}
                    onCampaignChange={handleCampaignChange}
                    templates={templates}
                    communicationEndpoints={endpoints}
                    timezones={timezones}
                  />
                </Grid>

                <Grid item xs={12}>
                  <OptOutConfig
                    config={{
                      enable_opt_out: formData.enable_opt_out,
                      initial_opt_out_notice: formData.initial_opt_out_notice,
                      opt_out_message: formData.opt_out_message
                    }}
                    onChange={(updatedConfig) => {
                      setFormData(prev => ({
                        ...prev,
                        ...updatedConfig
                      }));
                    }}
                  />
                </Grid>

                {/* Retry Configuration */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, mt: 2 }}>
                    <RetryConfigurationPanel
                      retryStrategy={formData.retry_strategy_id}
                      maxRetries={formData.max_retries}
                      onRetryStrategyChange={(strategyId) => setFormData(prev => ({ ...prev, retry_strategy_id: strategyId }))}
                      onMaxRetriesChange={(maxRetries) => setFormData(prev => ({ ...prev, max_retries: maxRetries }))}
                      description="Configure retry behavior for all reminder messages (campaign-level default)"
                    />
                  </Paper>
                </Grid>
              </Grid>
            )}
          </CommunicationEndpointsProvider>
        );

      case 2:
        return (
          <ReminderTestConfig
            campaign={formData}
            templates={templates}
            onTest={handleTestCampaign}
            testStatus={testData.testStatus}
            testError={testData.testError}
          />
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Review Campaign Details
              </Typography>
              <Paper sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Basic Information</Typography>
                    <Typography>Name: {formData.name}</Typography>
                    <Typography>Description: {formData.description}</Typography>
                    <Typography>
                      Account:{' '}
                      {accounts.find((a) => a.id === formData.account)?.name}
                    </Typography>
                    <Typography>Campaign Type: Reminder Campaign</Typography>
                    <Typography>
                      Channel: {formData.channel.charAt(0).toUpperCase() + formData.channel.slice(1)}
                    </Typography>
                    <Typography>
                      Start Date: {formData.start_date || 'Not set'}
                    </Typography>
                    <Typography>
                      End Date: {formData.is_ongoing ? 'Ongoing' : (formData.end_date || 'Not set')}
                    </Typography>
                    <Typography>Active: {formData.active ? 'Yes' : 'No'}</Typography>
                    <Typography>Status: {formData.status}</Typography>
                  </Grid>

                  {formData.channel !== 'voice' && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1">Message Configuration</Typography>
                      {formData.template ? (
                        <Typography>
                          Template:{' '}
                          {templates.find((t) => t.id === formData.template)?.name}
                        </Typography>
                      ) : (
                        <Typography>Content: {formData.content}</Typography>
                      )}
                    </Grid>
                  )}

                  {formData.channel === 'voice' && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1">Voice Configuration</Typography>
                      {formData.reminder_schedule.reminder_times.map((rt, rtIndex) => (
                        <Box key={rtIndex} sx={{ ml: 2, mt: 1 }}>
                          <Typography variant="subtitle2">Reminder Time {rtIndex + 1}:</Typography>
                          {rt.messages.map((msg, msgIndex) => {
                            const voiceConfig = msg.voice_config;
                            return (
                              <Box key={msgIndex} sx={{ ml: 2, mt: 1 }}>
                                <Typography>Message {msgIndex + 1}:</Typography>
                                <Typography>Platform: {voiceConfig.platform}</Typography>
                                <Typography>Voice: {voiceConfig.voice_name}</Typography>
                                <Typography>Temperature: {voiceConfig.temperature}</Typography>
                                <Typography>Max Duration: {voiceConfig.max_duration} minutes</Typography>
                                {voiceConfig.platform_config?.task && (
                                  <Typography>Task: {voiceConfig.platform_config.task}</Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ))}
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Schedule Settings</Typography>
                    <Typography>
                      Timezone: {formData.reminder_schedule.timezone}
                    </Typography>
                    <Typography>
                      Business Hours Only:{' '}
                      {formData.reminder_schedule.business_hours_only ? 'Yes' : 'No'}
                    </Typography>
                    <Typography>
                      Use Relative Schedule:{' '}
                      {formData.reminder_schedule.use_relative_schedule ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Reminder Times:</Typography>
                    {formData.reminder_schedule.reminder_times.map((rt, index) => (
                      <Box key={index} sx={{ ml: 2, mt: 1 }}>
                        <Typography>Time: {rt.time}</Typography>
                        <Typography>Messages: {rt.messages.length}</Typography>
                      </Box>
                    ))}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Retry Configuration</Typography>
                    <Typography>
                      Retry Strategy: {formData.retry_strategy_id ? `Strategy ID: ${formData.retry_strategy_id}` : 'Default Strategy'}
                    </Typography>
                    <Typography>
                      Max Retries: {formData.max_retries ? formData.max_retries : 'Use strategy default'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Note</Typography>
                    <Typography>
                      After creating the campaign, you will be redirected to the lead selection page where you can configure which leads to include in this campaign.
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/crm/lead-nurturing')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" gutterBottom>
            Create Reminder Campaign
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
          )}
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => setCreateDialogOpen(true)}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Campaign'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      >
        <DialogTitle>Create Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Choose how you want to create this campaign:
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleSubmit(false)} color="primary">
            Create as Draft
          </Button>
          <Button onClick={() => handleSubmit(true)} color="success" variant="contained">
            Create & Activate
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreateReminderCampaign; 